import { z } from "zod";

import { identity } from "~/tools/identity";
import {
	type GetImpulseFormInput,
	type GetImpulseFormOutputVerbose,
	ImpulseFormShape,
	ImpulseFormUnit,
} from "~/tools/impulse-form";
import { isStringDirty } from "~/tools/is-string-dirty";
import { Template } from "~/tools/template";

import type { DashboardWidgetLink } from "../../../common/dashboard-spec";
import type { FormProcessor } from "../_form-processor";

type Spec = DashboardWidgetLink;

type FormState = ImpulseFormShape<{
	url: ImpulseFormUnit<string, ReadonlyArray<string>, Template<["${", "}"], null>>;
	name: ImpulseFormUnit<string, null, undefined | Template<["${", "}"], null>>;
	tooltip: ImpulseFormUnit<string, null, undefined | Template<["${", "}"], null>>;
	targetBlank: ImpulseFormUnit<boolean>;
}>;

type Input = GetImpulseFormInput<FormState>;

const DEFAULT = {
	url: "",
	name: "",
	tooltip: "",
	targetBlank: false,
} satisfies Input;

function init(initial: Input = DEFAULT): FormState {
	return ImpulseFormShape({
		url: ImpulseFormUnit(initial.url, {
			schema: z
				.string()
				.trim()
				.min(1, "URL cannot be empty")
				.transform((url) => Template<null>(url)),
			isInputDirty: isStringDirty,
		}),

		name: ImpulseFormUnit(initial.name, {
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template<null>(trimmed) : undefined;
			},
			isInputDirty: isStringDirty,
		}),

		tooltip: ImpulseFormUnit(initial.tooltip, {
			isInputDirty: isStringDirty,
			transform: (input) => {
				const trimmed = input.trim();

				return trimmed ? Template<null>(trimmed) : undefined;
			},
		}),

		targetBlank: ImpulseFormUnit(initial.targetBlank),
	});
}

function specToInput({ name, url, tooltip, targetBlank }: Spec): Input {
	return {
		name: name?.source ?? DEFAULT.name,
		url: url.source ?? DEFAULT.url,
		tooltip: tooltip?.source ?? DEFAULT.tooltip,
		targetBlank: targetBlank ?? DEFAULT.targetBlank,
	};
}

function verboseOutputToSpec({ name, url, tooltip, targetBlank }: GetImpulseFormOutputVerbose<FormState>): Spec {
	return {
		url: url ?? Template(""),
		name: name ?? undefined,
		tooltip: tooltip ?? undefined,
		targetBlank: targetBlank ?? undefined,
	};
}

const FormState = {
	init,
	specToInput,
	outputToSpec: identity,
	verboseOutputToSpec,
} satisfies FormProcessor<Spec, FormState, Input>;

export { FormState };
